/**
 * A shared coarse clock for elapsed-time text. One interval runs only while some component is using it,
 * and only the components that call `useClock` re-render when it ticks: the board and its tickets do not.
 */
import { useSyncExternalStore } from 'react';

export const CLOCK_TICK_MS = 15_000;

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | undefined;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === undefined) {
    timer = setInterval(() => listeners.forEach((notify) => notify()), CLOCK_TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

/** Changes once per tick; stable between ticks, as `useSyncExternalStore` requires. */
const snapshot = () => Math.floor(Date.now() / CLOCK_TICK_MS);

/** Re-renders the caller every tick. Read `Date.now()` after calling it. */
export function useClock(): number {
  return useSyncExternalStore(subscribe, snapshot);
}
